/**
 * Contract Choice Card
 *
 * Sales Navigator / Recruiter seats with several contracts: the login waits until the
 * user picks one (status `choose-contract`). One button per contract the device listed;
 * the pick goes into the chat, where the assistant forwards it via submitContractChoice.
 */

import React, { useState } from 'react';
import { Box, Paper, Typography, Button } from '@mui/material';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import { makeStyles } from 'tss-react/mui';
import { useTranslation } from 'react-i18next';
import { ContractChoiceCardData } from '../../../types/ai-chat.types';
import { useAIChat } from '../../../hooks/use-ai-chat';

interface ContractChoiceCardProps {
  data: ContractChoiceCardData;
}

const ContractChoiceCard: React.FC<ContractChoiceCardProps> = ({ data }) => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const { send, isSending } = useAIChat();
  const [chosen, setChosen] = useState<string | null>(null);

  const contractNames = (data.contractNames || []).filter((name) => name.trim());

  const choose = (contract: string) => {
    if (isSending || chosen) return;
    send(`Contract for account ${data.accountId}: ${contract}`);
    setChosen(contract);
  };

  return (
    <Paper className={classes.card}>
      <Box className={classes.cardHeader}>
        <AssignmentOutlinedIcon className={classes.cardIcon} />
        <Typography variant="subtitle2">{t('contractChoiceTitle', 'Choose your LinkedIn contract')}</Typography>
      </Box>

      <Typography variant="body2" color="textSecondary" className={classes.description}>
        {contractNames.length === 0
          ? t(
              'contractChoiceNoneListed',
              'The contracts are still being read from the device. Ask me for the status in a moment and they will show up here.',
            )
          : data.email
            ? t('contractChoiceHint', 'There are several LinkedIn contracts for {{email}}. Pick the one SAIA should sign in with — the login waits for your choice.', {
                email: data.email,
              })
            : t(
                'contractChoiceHintNoEmail',
                'There are several LinkedIn contracts for this account. Pick the one SAIA should sign in with — the login waits for your choice.',
              )}
      </Typography>

      <Box className={classes.options}>
        {contractNames.map((contract) => (
          <Button
            key={contract}
            size="small"
            variant={chosen === contract ? 'contained' : 'outlined'}
            onClick={() => choose(contract)}
            disabled={isSending || (chosen !== null && chosen !== contract)}
            className={classes.option}
          >
            {contract}
          </Button>
        ))}
      </Box>

      {chosen && (
        <Typography variant="caption" color="textSecondary" className={classes.submitted}>
          {t('contractChoiceSubmitted', 'Contract "{{contract}}" selected — the login continues...', { contract: chosen })}
        </Typography>
      )}
    </Paper>
  );
};

const useStyles = makeStyles()((theme) => ({
  card: {
    padding: theme.spacing(1.5),
    borderRadius: 8,
    backgroundColor: '#fff',
    border: `1px solid ${theme.palette.warning.light}`,
  },
  cardHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  cardIcon: {
    fontSize: '1.25rem',
    color: theme.palette.warning.main,
  },
  description: {
    marginBottom: theme.spacing(1.5),
  },
  options: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: theme.spacing(1),
  },
  option: {
    textTransform: 'none',
    justifyContent: 'flex-start',
  },
  submitted: {
    display: 'block',
    marginTop: theme.spacing(1),
  },
}));

export default ContractChoiceCard;
